const DEFAULT_LANES = ['kick', 'snare', 'hihat', 'clap', 'perc'];

export const drumLaneTrackId = (index) => `drum-lane-${index}`;

export const drumLaneIndexFromTrackId = (trackId) => {
  const match = /^drum-lane-(\d+)$/.exec(String(trackId || ''));
  return match ? Number(match[1]) : null;
};

export const isDrumLaneClip = (clip) => clip?.clipType === 'pattern'
  && clip?.clipData?.kind !== 'instrument'
  && Number.isInteger(clip?.clipData?.drumLaneIndex);

const rowsForClip = (clip) => {
  const source = clip?.clipData || {};
  const rows = Array.isArray(source.drumSounds) && source.drumSounds.length
    ? source.drumSounds : DEFAULT_LANES.map((key) => ({ key, name: key }));
  const contentIndices = [
    ...Object.keys(source.pattern || {}).map((key) => Number(String(key).split('-')[0])),
    ...Object.keys(source.noteData || {}).map((key) => Number(String(key).split('-')[0])),
    ...(Array.isArray(source.noteEvents) ? source.noteEvents : []).map((event) => Number(event?.drumIndex))
  ].filter((index) => Number.isInteger(index) && index >= 0);
  const count = Math.max(rows.length, contentIndices.length ? Math.max(...contentIndices) + 1 : 0);
  return Array.from({ length: count }, (_, index) => rows[index] || {
    key: `drum-${index}`, name: `Drum ${index + 1}`, playbackMode: 'drum'
  });
};

/** Convert one legacy multi-sound clip into independently editable lane clips. */
export function expandGroupedDrumClip(clip) {
  if (isDrumLaneClip(clip)) {
    const trackId = drumLaneTrackId(clip.clipData.drumLaneIndex);
    return [clip.trackId === trackId ? clip : { ...clip, trackId }];
  }
  if (clip?.clipType !== 'pattern' || clip?.trackId !== 'pattern-track'
    || clip?.clipData?.kind === 'instrument') {
    return [clip];
  }
  const rows = rowsForClip(clip);
  const source = clip.clipData || {};
  return rows.map((row, index) => {
    const pattern = Object.fromEntries(Object.entries(source.pattern || {})
      .filter(([key]) => Number(String(key).split('-')[0]) === index));
    const noteData = Object.fromEntries(Object.entries(source.noteData || {})
      .filter(([key]) => Number(String(key).split('-')[0]) === index));
    const noteEvents = Array.isArray(source.noteEvents)
      ? source.noteEvents.filter((event) => Number(event?.drumIndex) === index)
      : undefined;
    return {
      ...clip,
      id: index === 0 ? clip.id : `${clip.id}::drum:${index}`,
      trackId: drumLaneTrackId(index),
      clipData: {
        ...source,
        name: row?.name || source.name || `Drum ${index + 1}`,
        drumSounds: rows,
        drumLaneIndex: index,
        drumLaneKey: row?.key || String(index),
        pattern,
        noteData,
        ...(Array.isArray(source.noteEvents) && source.noteEvents.length
          ? { noteEventsAuthoritative: true } : {}),
        ...(noteEvents ? { noteEvents } : {})
      }
    };
  });
}

export function expandGroupedDrumClips(clips = []) {
  // The live timeline is already migrated. Preserve its identity during edits
  // so a no-op position gesture does not redraw every track or reschedule audio.
  if (clips.every((clip) => (
    !(clip?.clipType === 'pattern' && clip?.trackId === 'pattern-track'
      && clip?.clipData?.kind !== 'instrument')
    && (!isDrumLaneClip(clip) || clip.trackId === drumLaneTrackId(clip.clipData.drumLaneIndex))
  ))) return clips;
  return clips.flatMap(expandGroupedDrumClip);
}

/** Sound design belongs to the lane and is shared by its clips. */
export function patchDrumLaneClipData(clipData, laneIndex, patch) {
  if (!clipData || !Number.isInteger(laneIndex) || !patch) return clipData;
  const rows = [...(clipData.drumSounds || [])];
  const current = rows[laneIndex] || {};
  rows[laneIndex] = { ...current, ...patch };
  const key = rows[laneIndex].key;
  return {
    ...clipData,
    drumSounds: rows,
    ...(typeof patch.cutMode === 'boolean' && key ? {
      drumCutMode: { ...(clipData.drumCutMode || {}), [key]: patch.cutMode }
    } : {})
  };
}

/** The lane catalog belongs to the session, not its first pattern clip. */
export function collectDrumLaneSounds(clips = [], fallback = []) {
  const patterns = clips.filter(clip => clip?.clipType === 'pattern'
    && clip.clipData?.kind !== 'instrument');
  const catalogs = patterns.map(clip => clip.clipData?.drumSounds || []);
  const count = Math.max(fallback.length, 0, ...catalogs.map(rows => rows.length),
    ...patterns.filter(isDrumLaneClip).map(clip => clip.clipData.drumLaneIndex + 1));
  const ownClips = new Map(patterns.filter(isDrumLaneClip)
    .map(clip => [clip.clipData.drumLaneIndex, clip]));
  return Array.from({ length: count }, (_, index) => {
    const own = ownClips.get(index);
    const row = own?.clipData?.drumSounds?.[index]
      || catalogs.find(rows => rows[index])?.[index] || fallback[index];
    return row || { key: own?.clipData?.drumLaneKey || `drum-${index}`,
      name: own?.clipData?.name || `Drum ${index + 1}`, playbackMode: 'drum' };
  });
}
